/**
 * Animal Service - Handles all animal-related operations in Firestore
 */

import firebase from "firebase/compat/app";
import { db } from "../firebase";
import { handleFirestoreError } from "../utils";
import {
	createCacheKey,
	fetchWithCache,
	invalidateCache,
} from "../../../utils/firestoreCache";
import {
	isAnimalActive,
	normalizeAnimalSuspiciousFlags,
} from "../../../utils/animalUtils";
import { normalizeProductionType } from "../../../constants/productionTypes";
import { buildHealthReviewUpdate } from "../../../utils/healthReview";

const ANIMAL_CACHE_NAMESPACE = "dbAnimals";
const ANIMAL_CACHE_MS = 45_000; // 45 seconds - balance between freshness and performance

function getTrimmedAnimalId(animal) {
	if (!animal || animal.ID === undefined || animal.ID === null) {
		return "";
	}

	return String(animal.ID).trim();
}

function hasValidAnimalId(animal) {
	return getTrimmedAnimalId(animal).length > 0;
}

/**
 * Fetches all registered animals for a given user
 * @param {string} userID - The unique identifier for the user
 * @param {Object} options - Options object
 * @param {boolean} options.forceRefresh - Whether to bypass cache
 * @returns {Promise<Array>} Array of animal objects with normalized data
 */
export async function getAnimals(userID, options = {}) {
	const { forceRefresh = false } = options;
	const cacheKey = createCacheKey(ANIMAL_CACHE_NAMESPACE, userID, "all");

	return fetchWithCache(
		cacheKey,
		async () => {
			try {
				const snapshot = await db
					.collection("Users")
					.doc(userID)
					.collection("Animals")
					.get();

				const animalPromises = snapshot.docs.map(async (animalDoc) => {
					// Process animal data and normalize Active field
					const animal = animalDoc.data();
					const trimmedId = getTrimmedAnimalId(animal);

					if (!animal || Object.keys(animal).length === 0 || !hasValidAnimalId(animal)) {
						return null;
					}

					if (animal.ID !== trimmedId) {
						animal.ID = trimmedId;
					}
					
					// Normalize Active to the canonical "Sending"/"Inactive" pair.
					// Detection is delegated to isAnimalActive so every accepted input
					// format ("Sending", true, "true") is recognised identically here and
					// in the UI.
					animal.Active = isAnimalActive(animal) ? "Sending" : "Inactive";
					
					// Normalize suspicious flags based on HealthScore threshold (UI-only, non-persistent)
					normalizeAnimalSuspiciousFlags(animal);

					// Animals created before the field existed are calves (non-persistent default)
					if (animal.SchemaVersion !== 2) animal.ProductionType = normalizeProductionType(animal.ProductionType);

					return animal;
				});

				const animals = await Promise.all(animalPromises);
				return animals.filter(Boolean);
			} catch (error) {
				handleFirestoreError(error);
			}
		},
		{ ttlMs: ANIMAL_CACHE_MS, forceRefresh }
	);
}

/**
 * Invalidates the animal cache for a user
 * Call this after mutations (create, update, delete, archive)
 * @param {string} userID - The unique identifier for the user
 */
export function invalidateAnimalCache(userID) {
	if (!userID) return;
	invalidateCache(`${ANIMAL_CACHE_NAMESPACE}:["${userID}"`);
}

/**
 * Fetches all archived animals for a given user
 * Checks both ArchivedAnimals (current) and Archive (legacy) collections for backward compatibility
 * @param {string} userID - The unique identifier for the user
 * @returns {Promise<Array>} Array of archived animal objects
 */
export async function getArchivedAnimals(userID) {
	try {
		// Get animals from both ArchivedAnimals (current) and Archive (legacy) collections
		const [archivedSnapshot, legacySnapshot] = await Promise.all([
			db
				.collection("Users")
				.doc(userID)
				.collection("ArchivedAnimals")
				.get()
				.catch((e) => {
					console.error("[animalService][getArchivedAnimals] - Error getting ArchivedAnimals:", e);
					return { docs: [] };
				}),
			db
				.collection("Users")
				.doc(userID)
				.collection("Archive")
				.get()
				.catch((e) => {
					console.error("[animalService][getArchivedAnimals] - Error getting Archive (legacy):", e);
					return { docs: [] };
				})
		]);

		// Combine documents from both collections, prioritizing ArchivedAnimals
		const allDocs = [...archivedSnapshot.docs];
		
		// Add legacy Archive docs only if they don't exist in ArchivedAnimals (by MAC)
		const archivedMACs = new Set(archivedSnapshot.docs.map(doc => doc.id));
		legacySnapshot.docs.forEach(doc => {
			if (!archivedMACs.has(doc.id)) {
				allDocs.push(doc);
			}
		});

		const animalPromises = allDocs.map(async (animalDoc) => {
			let animal = animalDoc.data();

			if (animal.ID) {
				animal.Active = isAnimalActive(animal) ? "Sending" : "Inactive";
				animal.Group = animal.Group ? animal.Group : "-";
				animal.Location = animal.Location ? animal.Location : "-";

				return animal;
			} else {
				return null;
			}
		});

		const animals = (await Promise.all(animalPromises)).filter(
			(animal) => animal !== null
		);

		return animals;
	} catch (error) {
		handleFirestoreError(error);
		return [];
	}
}

/**
 * Gets animals of a specific group
 * @param {string} userID - The unique identifier for the user
 * @param {string} group - The group name
 * @returns {Promise<Array>} Array of animal objects in the specified group
 */
export async function getAnimalsOfGroup(userID, group) {
	try {
		const snapshot = await db
			.collection("Users")
			.doc(userID)
			.collection("Animals")
			.where("Group", "==", group)
			.get();

		const animalPromises = snapshot.docs.map(async (doc) => {
			const animal = doc.data();
			const trimmedId = getTrimmedAnimalId(animal);

			if (!animal || Object.keys(animal).length === 0 || !hasValidAnimalId(animal)) {
				return null;
			}

			if (animal.ID !== trimmedId) {
				animal.ID = trimmedId;
			}
			
			// Normalize Active to the canonical "Sending"/"Inactive" pair (see getAnimals).
			animal.Active = isAnimalActive(animal) ? "Sending" : "Inactive";
			
			// Normalize suspicious flags based on HealthScore threshold (UI-only, non-persistent)
			normalizeAnimalSuspiciousFlags(animal);
			
			return animal;
		});

		const animals = await Promise.all(animalPromises);
		return animals.filter(Boolean);
	} catch (error) {
		handleFirestoreError(error);
		return []; // Return empty array instead of undefined
	}
}

/**
 * Sets an animal to inactive
 * @param {string} userID - The unique identifier for the user
 * @param {string} mac - The MAC address of the animal's device
 * @returns {Promise<void>}
 */
export async function setAnimalInactive(userID, mac) {
	try {
		await db
			.collection("Users")
			.doc(userID)
			.collection("Animals")
			.doc(mac)
			.update({ isActive: false });
	} catch (error) {
		handleFirestoreError(error);
	}
}

/**
 * Generates a placeholder MAC address for animals without physical eartags
 * Format: 00:00:00:XX:XX:XX (standard MAC format with 3 random bytes)
 * @param {string} userID - The unique identifier for the user (unused but kept for compatibility)
 * @returns {string} Placeholder MAC address in format 00:00:00:XX:XX:XX
 */
export function generatePlaceholderMAC(userID) {
	// Generate 3 random bytes (6 hex characters)
	const randomBytes = new Uint8Array(3);
	if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
		crypto.getRandomValues(randomBytes);
	} else {
		// Fallback for environments without crypto API
		for (let i = 0; i < 3; i++) {
			randomBytes[i] = Math.floor(Math.random() * 256);
		}
	}
	
	// Convert to hex and format as MAC address
	const hexSuffix = Array.from(randomBytes)
		.map(b => b.toString(16).padStart(2, '0'))
		.join(':')
		.toUpperCase();
	
	return `00:00:00:${hexSuffix}`;
}

/**
 * Checks if a tag (MAC address) is available for use
 * @param {string} userID - The unique identifier for the user
 * @param {string} mac - The MAC address to check
 * @returns {Promise<{available: boolean, reason?: string, inArchive?: boolean}>}
 */
export async function checkTagAvailability(userID, mac) {
	try {
		// Check if MAC exists in active Animals collection
		const activeDoc = await db
			.collection("Users")
			.doc(userID)
			.collection("Animals")
			.doc(mac)
			.get();

		if (activeDoc.exists) {
			return {
				available: false,
				reason: "Tag is already in use by an active animal",
				inArchive: false
			};
		}

		// Check if MAC exists in ArchivedAnimals collection
		const archivedDoc = await db
			.collection("Users")
			.doc(userID)
			.collection("ArchivedAnimals")
			.doc(mac)
			.get();

		if (archivedDoc.exists) {
			// Tag is archived, allow reuse
			return {
				available: true,
				reason: "Tag is archived and can be reused",
				inArchive: true
			};
		}

		// Tag doesn't exist, allow use
		return {
			available: true,
			reason: "Tag is available",
			inArchive: false
		};
	} catch (error) {
		console.error("[animalService][checkTagAvailability] - Error checking tag availability:", error);
		// On error, allow use but log it
		return {
			available: true,
			reason: "Error checking availability, proceeding",
			inArchive: false
		};
	}
}

/**
 * Finds duplicate animals with the same MAC address
 * @param {string} userID - The unique identifier for the user
 * @param {string} mac - The MAC address to check
 * @returns {Promise<Array>} Array of duplicate animal documents
 */
export async function findDuplicateAnimals(userID, mac) {
	try {
		const duplicates = [];
		
		// Check active animals
		const activeSnapshot = await db
			.collection("Users")
			.doc(userID)
			.collection("Animals")
			.where("MAC", "==", mac)
			.get();

		activeSnapshot.forEach((doc) => {
			duplicates.push({ ...doc.data(), docId: doc.id, collection: "Animals" });
		});

		// Also check by document ID (MAC is used as doc ID)
		const activeDoc = await db
			.collection("Users")
			.doc(userID)
			.collection("Animals")
			.doc(mac)
			.get();

		if (activeDoc.exists) {
			const exists = duplicates.find(d => d.docId === mac);
			if (!exists) {
				duplicates.push({ ...activeDoc.data(), docId: activeDoc.id, collection: "Animals" });
			}
		}

		return duplicates;
	} catch (error) {
		console.error("[animalService][findDuplicateAnimals] - Error finding duplicates:", error);
		return [];
	}
}

/**
 * Checks if an animal ID already exists in the system
 * Checks both active Animals and ArchivedAnimals collections
 * @param {string} userID - The unique identifier for the user
 * @param {string} animalId - The animal ID to check (will be trimmed and normalized)
 * @returns {Promise<{exists: boolean, isArchived: boolean}>} Object indicating if ID exists and where
 */
export async function checkAnimalIdExists(userID, animalId) {
	try {
		// Normalize the ID: trim whitespace and convert to string
		const normalizedId = String(animalId).trim();
		
		if (!normalizedId) {
			return { exists: false, isArchived: false };
		}
		
		// Check active Animals collection
		const activeSnapshot = await db
			.collection("Users")
			.doc(userID)
			.collection("Animals")
			.where("ID", "==", normalizedId)
			.get();
		
		if (!activeSnapshot.empty) {
			return { exists: true, isArchived: false };
		}
		
		// Check ArchivedAnimals collection
		const archivedSnapshot = await db
			.collection("Users")
			.doc(userID)
			.collection("ArchivedAnimals")
			.where("ID", "==", normalizedId)
			.get();
		
		if (!archivedSnapshot.empty) {
			return { exists: true, isArchived: true };
		}
		
		// Also check legacy Archive collection for backward compatibility
		const legacyArchiveSnapshot = await db
			.collection("Users")
			.doc(userID)
			.collection("Archive")
			.where("ID", "==", normalizedId)
			.get()
			.catch((e) => {
				console.error("[animalService][checkAnimalIdExists] - Error checking legacy Archive:", e);
				return { empty: true };
			});
		
		if (legacyArchiveSnapshot && !legacyArchiveSnapshot.empty) {
			return { exists: true, isArchived: true };
		}
		
		return { exists: false, isArchived: false };
	} catch (error) {
		console.error("[animalService][checkAnimalIdExists] - Error checking animal ID:", error);
		// On error, return exists: false to allow creation (fail-safe)
		return { exists: false, isArchived: false };
	}
}

/**
 * Updates or creates an animal record
 * Validates tag availability and allows reuse of archived tags
 * @param {string} userID - The unique identifier for the user
 * @param {object} animal - The animal data to set
 * @param {string} [animal.MAC] - MAC address (auto-generated if not provided)
 * @param {boolean} [allowArchivedReuse=true] - Allow reusing archived tags
 * @param {boolean} [isDeliveryNoteImport=false] - Whether the record comes from delivery note import
 * @param {object|null} [stockInOptions=null] - Optional stock-in context payload
 * @returns {Promise<string>} The MAC address used (for the created animal)
 */
export async function setAnimal(
	userID,
	animal,
	allowArchivedReuse = true,
	isDeliveryNoteImport = false,
	stockInOptions = null
) {
	try {
		// Auto-generate placeholder MAC if not provided
		if (animal.SchemaVersion === 2) throw new Error('cattle-update-required');
		if (!animal.MAC) {
			animal.MAC = generatePlaceholderMAC(userID);
		}
		
		// Check if a real eartag (non-placeholder MAC) was registered
		// Real MAC addresses don't start with "PLACEHOLDER:" or "00:00:00:"
		const isRealMAC = animal.MAC && 
			!animal.MAC.startsWith("PLACEHOLDER:") && 
			!animal.MAC.startsWith("00:00:00:");
		
		// For real MAC addresses, check tag availability (only for NEW animals)
		if (isRealMAC && allowArchivedReuse) {
			const existingDoc = await db
				.collection("Users")
				.doc(userID)
				.collection("Animals")
				.doc(animal.MAC)
				.get();

			// Only check availability if this is a NEW animal (document doesn't exist yet)
			if (existingDoc.exists && existingDoc.data()?.SchemaVersion === 2) throw new Error('cattle-update-required');
			if (!existingDoc.exists) {
				const availability = await checkTagAvailability(userID, animal.MAC);

				if (!availability.available && !availability.inArchive) {
					// Tag is in use by active animal - this is an error
					throw new Error(`Tag ${animal.MAC} is already in use by an active animal`);
				}

				// If tag is archived and we're reusing it, ensure Uid matches MAC
				// This handles the case where mobile app registers with UID instead of MAC
				if (availability.inArchive) {
					console.log(`[animalService][setAnimal] - Reusing archived tag ${animal.MAC}`);
					// Ensure MAC is used as primary identifier, not UID
					if (animal.Uid && animal.Uid !== animal.MAC) {
						console.warn(`[animalService][setAnimal] - UID mismatch detected. UID: ${animal.Uid}, MAC: ${animal.MAC}. Using MAC as primary identifier.`);
					}
				}
			}
		}
		
		// If real MAC detected, remove noEartags flag (user now has eartags)
		if (isRealMAC) {
			try {
				const userDoc = await db.collection("Users").doc(userID).get();
				
				if (userDoc.exists) {
					const userData = userDoc.data();
					// If user has noEartags flag set to true, remove it (they now have eartags)
					if (userData?.noEartags === true) {
						await db.collection("Users").doc(userID).update({
							noEartags: false,
							UpdatedAt: firebase.firestore.FieldValue.serverTimestamp()
						});
						console.log('[animalService][setAnimal] - Eartag detected, removed noEartags flag');
					}
				}
			} catch (error) {
				console.error("[animalService][setAnimal] - Error updating noEartags flag:", error);
				// Don't fail animal creation if flag update fails
			}
		}
		
		// Validate SelectionEarTag uniqueness if being set
		if (animal.SelectionEarTag) {
			const tagCheck = await checkSelectionEarTagExists(userID, animal.SelectionEarTag);
			if (tagCheck.exists && tagCheck.animalMAC !== animal.MAC) {
				throw new Error(`SelectionEarTag "${animal.SelectionEarTag}" is already assigned to another animal (MAC: ${tagCheck.animalMAC})`);
			}
		}

		// Ensure Uid field matches MAC if not set (or if UID was incorrectly set)
		// This fixes the mobile app issue where UID is used instead of MAC
		if (!animal.Uid || (isRealMAC && animal.Uid !== animal.MAC)) {
			animal.Uid = animal.MAC;
		}
		
		// Ensure Active is set to "Sending" for re-tagged animals
		if (!animal.Active) {
			animal.Active = "Sending";
		}

		const animalRef = db
			.collection("Users")
			.doc(userID)
			.collection("Animals")
			.doc(animal.MAC);

		const existingAnimalDoc = await animalRef.get().catch((e) => {
			throw e;
		});
		const isCreate = !existingAnimalDoc.exists;
		if (animal.SchemaVersion === 2 || (existingAnimalDoc.exists && existingAnimalDoc.data()?.SchemaVersion === 2)) throw new Error('cattle-update-required');

		// Persist a production type on creation only — updates must not
		// overwrite an existing type with the default
		if (isCreate) {
			animal.ProductionType = normalizeProductionType(animal.ProductionType);
		}

		await animalRef.set(animal, { merge: true });
		
		// Invalidate cache after animal mutation
		invalidateAnimalCache(userID);
		
		// Update farm size in billing after animal creation
		try {
			const { updateFarmSizeInBilling } = await import("./billingService");
			await updateFarmSizeInBilling(userID);
			// Note: canAddAnimal check should be done before calling setAnimal, not after
		} catch (error) {
			console.error("[animalService][setAnimal] - Error updating farm size:", error);
			// Don't fail animal creation if billing update fails
		}

		// Create stock-in transaction only for new animals.
		if (isCreate) {
			try {
				const { createStockInEvent } = await import("./stockEventService");
				const context =
					stockInOptions && typeof stockInOptions === "object"
						? stockInOptions
						: {};

				const defaultSource = isDeliveryNoteImport ? "delivery-note" : "create-animal";
				const stockInPayload = {
					MAC: animal.MAC,
					ID: animal.ID || animal.MAC,
					Sex: animal.Sex,
					StartDate:
						context.StartDate ||
						animal.EntryDate ||
						new Date().toISOString().split("T")[0],
					...(context.StartTime ? { StartTime: context.StartTime } : {}),
					reasonPrimary: context.reasonPrimary || "admin_correction",
					...(context.reasonSecondary
						? { reasonSecondary: context.reasonSecondary }
						: {}),
					...(context.cullDriverSubtype
						? { cullDriverSubtype: context.cullDriverSubtype }
						: {}),
					...(context.note ? { note: context.note } : {}),
					...(context.value ? { value: context.value } : {}),
					productionType: animal.ProductionType,
					partner: {
						supplier:
							context?.partner?.supplier ||
							context?.supplier ||
							animal.Supplier ||
							undefined,
						buyer: context?.partner?.buyer || context?.buyer || undefined,
					},
					source: context.source || defaultSource,
					inferred:
						context.reasonPrimary === undefined
							? true
							: Boolean(context.inferred),
					confidence:
						context.confidence ||
						(context.reasonPrimary ? "high" : "low"),
					reviewStatus:
						context.reviewStatus ||
						(context.reasonPrimary ? undefined : "pending"),
					status: context.status || "completed",
				};

				await createStockInEvent(userID, stockInPayload);
			} catch (stockError) {
				console.error(
					"[animalService][setAnimal] - Animal created but stock-in event creation failed:",
					stockError
				);
			}
		}
		
		return animal.MAC;
	} catch (error) {
		handleFirestoreError(error);
		throw error;
	}
}

/**
 * Fetches a specific animal by MAC address
 * Checks both active Animals and ArchivedAnimals collections
 * @param {string} userID - The unique identifier for the user
 * @param {string} mac - The MAC address of the animal's device
 * @returns {Promise<object>} The animal data, or null if not found
 */
/**
 * Looks up an animal by its display ID (e.g., "0003") and returns its MAC address.
 * Searches active Animals first, then ArchivedAnimals.
 * @param {string} userID - The user's unique identifier
 * @param {string} displayId - The animal's display ID (e.g., "0003")
 * @returns {Promise<string|null>} The MAC address if found, null otherwise
 */
export async function getAnimalMacByDisplayId(userID, displayId) {
	try {
		// Search active animals by ID field
		const activeSnap = await db
			.collection("Users")
			.doc(userID)
			.collection("Animals")
			.where("ID", "==", displayId)
			.limit(1)
			.get();

		if (!activeSnap.empty) {
			return activeSnap.docs[0].id;
		}

		// Search archived animals by ID field
		const archivedSnap = await db
			.collection("Users")
			.doc(userID)
			.collection("ArchivedAnimals")
			.where("ID", "==", displayId)
			.limit(1)
			.get();

		if (!archivedSnap.empty) {
			return archivedSnap.docs[0].id;
		}

		return null;
	} catch (error) {
		console.error("[animalService][getAnimalMacByDisplayId] - Error:", error);
		return null;
	}
}

export async function getAnimal(userID, mac) {
	try {
		// First try to get from active Animals collection
		const doc = await db
			.collection("Users")
			.doc(userID)
			.collection("Animals")
			.doc(mac)
			.get();

		if (doc.exists) {
			const animal = doc.data();
			if (animal.SchemaVersion !== 2) animal.ProductionType = normalizeProductionType(animal.ProductionType);
			return animal;
		}

		// If not found in active, check ArchivedAnimals collection
		const archivedDoc = await db
			.collection("Users")
			.doc(userID)
			.collection("ArchivedAnimals")
			.doc(mac)
			.get();

		if (archivedDoc.exists) {
			const animal = archivedDoc.data();
			if (animal.SchemaVersion !== 2) animal.ProductionType = normalizeProductionType(animal.ProductionType);
			return animal;
		}

		return null;
	} catch (error) {
		handleFirestoreError(error);
		return null;
	}
}

/**
 * Archives an animal (moves it from active to archived collection)
 * Copies all subcollections to archived animal before deleting originals
 * @param {string} userId - The unique identifier for the user
 * @param {string} mac - The MAC address of the animal's device
 * @param {Function} [onProgress] - Optional progress callback: (current, total, message) => void
 * @param {string} [reason] - Optional reason for archiving
 * @returns {Promise<void>}
 */
export async function archiveAnimal(userId, mac, onProgress = null, reason = null) {
	try {
		const animalRef = db
			.collection("Users")
			.doc(userId)
			.collection("Animals")
			.doc(mac);
		const animalSnapshot = await animalRef.get().catch((e) => {
			throw e;
		});

		if (!animalSnapshot.exists) {
			throw new Error(`Animal with MAC ${mac} not found`);
		}

		const subcollections = ["Positions", "Birth", "Heat", "Sensordata", "Health", "Notes", "EventHistory", "GroupHistory", "Nodes", "SuspiciousHistory"];
		
		// Step 1: Copy all subcollections to archived animal
		const archivedAnimalRef = db
			.collection("Users")
			.doc(userId)
			.collection("ArchivedAnimals")
			.doc(mac);

		let totalDocs = 0;
		let copiedDocs = 0;

		// First, count total documents across all subcollections
		if (onProgress) {
			onProgress(0, 0, "Counting subcollections...");
		}

		const countPromises = subcollections.map(async (subcollection) => {
			const subcollectionRef = animalRef.collection(subcollection);
			const snapshot = await subcollectionRef.get().catch((e) => {
				console.error(`[animalService][archiveAnimal] - Error counting ${subcollection}:`, e);
				return null;
			});
			return snapshot ? snapshot.size : 0;
		});

		const counts = await Promise.all(countPromises);
		totalDocs = counts.reduce((sum, count) => sum + count, 0);

		if (onProgress) {
			onProgress(0, totalDocs, `Copying ${totalDocs} documents from ${subcollections.length} subcollections...`);
		}

		// Copy all subcollections
		let copyBatch = db.batch();
		let batchCount = 0;
		const BATCH_SIZE = 500; // Firestore batch limit

		for (let i = 0; i < subcollections.length; i++) {
			const subcollection = subcollections[i];
			const subcollectionRef = animalRef.collection(subcollection);
			const archivedSubcollectionRef = archivedAnimalRef.collection(subcollection);
			
			try {
				const snapshot = await subcollectionRef.get().catch((e) => {
					console.error(`[animalService][archiveAnimal] - Error getting ${subcollection} subcollection:`, e);
					return null;
				});
				
				if (snapshot && !snapshot.empty) {
					for (const doc of snapshot.docs) {
						// Copy document to archived subcollection
						copyBatch.set(archivedSubcollectionRef.doc(doc.id), doc.data());
						batchCount++;
						copiedDocs++;

						// Commit batch if we hit the limit
						if (batchCount >= BATCH_SIZE) {
							await copyBatch.commit();
							if (onProgress) {
								onProgress(copiedDocs, totalDocs, `Copied ${copiedDocs}/${totalDocs} documents...`);
							}
							// Start new batch
							copyBatch = db.batch();
							batchCount = 0;
						}
					}
				}
			} catch (error) {
				console.error(`[animalService][archiveAnimal] - Error copying ${subcollection}:`, error);
				// Continue with other subcollections
			}
		}

		// Commit any remaining documents in batch
		if (batchCount > 0) {
			await copyBatch.commit();
			if (onProgress) {
				onProgress(copiedDocs, totalDocs, `Copied ${copiedDocs}/${totalDocs} documents...`);
			}
		}

		// Step 2: Create archived animal document
		if (onProgress) {
			onProgress(totalDocs, totalDocs, "Archiving animal...");
		}

		let archivedAnimal = animalSnapshot.data();
		archivedAnimal.ArchivedAt = new Date().getTime();
		if (reason) {
			archivedAnimal.ArchiveReason = reason;
		}

		await db
			.collection("Users")
			.doc(userId)
			.collection("ArchivedAnimals")
			.doc(mac)
			.set(archivedAnimal)
			.catch((e) => {
				throw e;
			});

		// Step 3: Delete original subcollections and animal
		if (onProgress) {
			onProgress(totalDocs + 1, totalDocs + 2, "Deleting original data...");
		}

		let deleteBatch = db.batch();
		batchCount = 0;

		for (const subcollection of subcollections) {
			const subcollectionRef = animalRef.collection(subcollection);
			try {
				const snapshot = await subcollectionRef.get().catch((e) => {
					console.error(`[animalService][archiveAnimal] - Error getting ${subcollection} for deletion:`, e);
					return null;
				});
				
				if (snapshot) {
					for (const doc of snapshot.docs) {
						deleteBatch.delete(doc.ref);
						batchCount++;

						// Commit batch if we hit the limit
						if (batchCount >= BATCH_SIZE) {
							await deleteBatch.commit();
							// Start new batch
							deleteBatch = db.batch();
							batchCount = 0;
						}
					}
				}
			} catch (error) {
				console.error(`[animalService][archiveAnimal] - Error deleting ${subcollection}:`, error);
				// Continue with other subcollections
			}
		}

		// Commit any remaining deletions
		if (batchCount > 0) {
			await deleteBatch.commit();
		}

		// Delete the animal document
		await animalRef.delete().catch((e) => {
			throw e;
		});

		if (onProgress) {
			onProgress(totalDocs + 2, totalDocs + 2, "Archive complete!");
		}
		
		// Invalidate cache after animal archiving
		invalidateAnimalCache(userId);
		
		// Update farm size in billing after animal archiving
		try {
			const { updateFarmSizeInBilling } = await import("./billingService");
			await updateFarmSizeInBilling(userId);
		} catch (error) {
			console.error("[animalService][archiveAnimal] - Error updating farm size:", error);
			// Don't fail animal archiving if billing update fails
		}
	} catch (error) {
		handleFirestoreError(error);
		throw error;
	}
}

/**
 * Reactivates an archived animal (moves it from ArchivedAnimals to Animals)
 * Copies all subcollections back to active animal before deleting from archive
 * @param {string} userId - The unique identifier for the user
 * @param {string} mac - The MAC address of the animal's device
 * @param {Function} [onProgress] - Optional progress callback: (current, total, message) => void
 * @returns {Promise<void>}
 */
export async function reactivateAnimal(userId, mac, onProgress = null) {
	try {
		// Step 0: Validate that animal doesn't already exist in Animals collection
		const activeAnimalRef = db
			.collection("Users")
			.doc(userId)
			.collection("Animals")
			.doc(mac);
		
		const activeAnimalSnapshot = await activeAnimalRef.get().catch((e) => {
			throw e;
		});

		if (activeAnimalSnapshot.exists) {
			throw new Error(`Animal with MAC ${mac} already exists in active collection. Cannot reactivate.`);
		}

		// Step 1: Find archived animal in ArchivedAnimals or legacy Archive collection
		const archivedAnimalRef = db
			.collection("Users")
			.doc(userId)
			.collection("ArchivedAnimals")
			.doc(mac);
		
		const legacyArchiveRef = db
			.collection("Users")
			.doc(userId)
			.collection("Archive")
			.doc(mac);

		// Try ArchivedAnimals first, then legacy Archive
		let archivedAnimalSnapshot = await archivedAnimalRef.get().catch((e) => {
			console.error("[animalService][reactivateAnimal] - Error getting from ArchivedAnimals:", e);
			return null;
		});

		let sourceRef = archivedAnimalRef;

		if (!archivedAnimalSnapshot || !archivedAnimalSnapshot.exists) {
			// Try legacy Archive collection
			archivedAnimalSnapshot = await legacyArchiveRef.get().catch((e) => {
				console.error("[animalService][reactivateAnimal] - Error getting from Archive (legacy):", e);
				return null;
			});
			
			if (archivedAnimalSnapshot && archivedAnimalSnapshot.exists) {
				sourceRef = legacyArchiveRef;
			}
		}

		if (!archivedAnimalSnapshot || !archivedAnimalSnapshot.exists) {
			throw new Error(`Archived animal with MAC ${mac} not found in ArchivedAnimals or Archive collection`);
		}

		const subcollections = ["Positions", "Birth", "Heat", "Sensordata", "Health", "Notes", "EventHistory", "GroupHistory", "Nodes", "SuspiciousHistory"];

		// Step 2: Copy all subcollections from archive back to active animal
		let totalDocs = 0;
		let copiedDocs = 0;

		// First, count total documents across all subcollections
		if (onProgress) {
			onProgress(0, 0, "Counting subcollections...");
		}

		const countPromises = subcollections.map(async (subcollection) => {
			const archivedSubcollectionRef = sourceRef.collection(subcollection);
			const snapshot = await archivedSubcollectionRef.get().catch((e) => {
				console.error(`[animalService][reactivateAnimal] - Error counting ${subcollection}:`, e);
				return null;
			});
			return snapshot ? snapshot.size : 0;
		});

		const counts = await Promise.all(countPromises);
		totalDocs = counts.reduce((sum, count) => sum + count, 0);

		if (onProgress) {
			onProgress(0, totalDocs, `Restoring ${totalDocs} documents from ${subcollections.length} subcollections...`);
		}

		// Copy all subcollections
		let copyBatch = db.batch();
		let batchCount = 0;
		const BATCH_SIZE = 500; // Firestore batch limit

		for (let i = 0; i < subcollections.length; i++) {
			const subcollection = subcollections[i];
			const archivedSubcollectionRef = sourceRef.collection(subcollection);
			const activeSubcollectionRef = activeAnimalRef.collection(subcollection);
			
			try {
				const snapshot = await archivedSubcollectionRef.get().catch((e) => {
					console.error(`[animalService][reactivateAnimal] - Error getting ${subcollection} subcollection:`, e);
					return null;
				});
				
				if (snapshot && !snapshot.empty) {
					for (const doc of snapshot.docs) {
						// Copy document to active subcollection
						copyBatch.set(activeSubcollectionRef.doc(doc.id), doc.data());
						batchCount++;
						copiedDocs++;

						// Commit batch if we hit the limit
						if (batchCount >= BATCH_SIZE) {
							await copyBatch.commit();
							if (onProgress) {
								onProgress(copiedDocs, totalDocs, `Restored ${copiedDocs}/${totalDocs} documents...`);
							}
							// Start new batch
							copyBatch = db.batch();
							batchCount = 0;
						}
					}
				}
			} catch (error) {
				console.error(`[animalService][reactivateAnimal] - Error copying ${subcollection}:`, error);
				// Continue with other subcollections
			}
		}

		// Commit any remaining documents in batch
		if (batchCount > 0) {
			await copyBatch.commit();
			if (onProgress) {
				onProgress(copiedDocs, totalDocs, `Restored ${copiedDocs}/${totalDocs} documents...`);
			}
		}

		// Step 3: Create reactivated animal document
		if (onProgress) {
			onProgress(totalDocs, totalDocs, "Reactivating animal...");
		}

		// Copy archived animal data
		let reactivatedAnimal = archivedAnimalSnapshot.data();
		// Remove archive-specific fields
		const { ArchivedAt, archivedAt, ...rest } = reactivatedAnimal;
		reactivatedAnimal = rest;
		reactivatedAnimal.ReactivatedAt = new Date().getTime();

		// Add the animal back to the "Animals" collection
		await activeAnimalRef.set(reactivatedAnimal).catch((e) => {
			throw e;
		});

		// Step 4: Delete archived animal and its subcollections
		if (onProgress) {
			onProgress(totalDocs + 1, totalDocs + 2, "Cleaning up archive...");
		}

		let deleteBatch = db.batch();
		batchCount = 0;

		for (const subcollection of subcollections) {
			const archivedSubcollectionRef = sourceRef.collection(subcollection);
			try {
				const snapshot = await archivedSubcollectionRef.get().catch((e) => {
					console.error(`[animalService][reactivateAnimal] - Error getting ${subcollection} for deletion:`, e);
					return null;
				});
				
				if (snapshot) {
					for (const doc of snapshot.docs) {
						deleteBatch.delete(doc.ref);
						batchCount++;

						// Commit batch if we hit the limit
						if (batchCount >= BATCH_SIZE) {
							await deleteBatch.commit();
							// Start new batch
							deleteBatch = db.batch();
							batchCount = 0;
						}
					}
				}
			} catch (error) {
				console.error(`[animalService][reactivateAnimal] - Error deleting ${subcollection}:`, error);
				// Continue with other subcollections
			}
		}

		// Commit any remaining deletions
		if (batchCount > 0) {
			await deleteBatch.commit();
		}

		// Delete the archived animal document
		await sourceRef.delete().catch((e) => {
			throw e;
		});

		if (onProgress) {
			onProgress(totalDocs + 2, totalDocs + 2, "Reactivation complete!");
		}
		
		// Invalidate cache after animal reactivation
		invalidateAnimalCache(userId);
		
		// Update farm size in billing after animal reactivation
		try {
			const { updateFarmSizeInBilling } = await import("./billingService");
			await updateFarmSizeInBilling(userId);
		} catch (error) {
			console.error("[animalService][reactivateAnimal] - Error updating farm size:", error);
			// Don't fail animal reactivation if billing update fails
		}
	} catch (error) {
		handleFirestoreError(error);
		throw error;
	}
}

/**
 * Updates specific fields of an animal document
 * @param {string} userID - The unique identifier for the user
 * @param {string} animalMAC - The MAC address of the animal's device
 * @param {object} updates - Object containing fields to update
 * @returns {Promise<void>}
 */
export async function updateAnimalFields(userID, animalMAC, updates) {
	try {
		await db
			.collection("Users")
			.doc(userID)
			.collection("Animals")
			.doc(animalMAC)
			.update(updates);
		
		// Invalidate cache after animal mutation
		invalidateAnimalCache(userID);
	} catch (error) {
		console.error("[animalService][updateAnimalFields] - Error updating animal fields:", error);
		handleFirestoreError(error);
		throw error;
	}
}

/**
 * Acknowledges the current Watchdog health finding of an animal
 * ("Befund zur Kenntnis nehmen").
 *
 * Besides the review timestamp the update stores a snapshot of the evidence the
 * farmer looked at (severity, reason codes, score), so the attention lists can
 * hide the animal while the finding is unchanged and bring it back as soon as
 * the Watchdog escalates it (see utils/healthReview.js).
 * @param {string} userID - The unique identifier for the user
 * @param {Object} animal - Animal document (needs `MAC` and the health fields)
 * @param {Object} [options]
 * @param {string} [options.reviewedBy] - Reviewer user id (defaults to userID)
 * @param {number} [options.reviewedAt] - Review time in epoch ms (defaults to now)
 * @returns {Promise<Object>} The update that was written
 */
export async function acknowledgeAnimalHealthFinding(
	userID,
	animal,
	{ reviewedBy = userID, reviewedAt = Date.now() } = {}
) {
	const mac = animal?.MAC;
	if (!userID || !mac) {
		throw new Error("[animalService][acknowledgeAnimalHealthFinding] - userID and animal.MAC are required");
	}

	const update = buildHealthReviewUpdate(animal, { reviewedAt, reviewedBy });
	await updateAnimalFields(userID, mac, update);
	return update;
}

/**
 * Updates an animal's BreedingGroup field
 * Prevents changing BreedingGroup if animal already has one (unless allowOverride is true)
 * @param {string} userID - The unique identifier for the user
 * @param {string} animalMAC - The MAC address of the animal's device
 * @param {string} breedingGroup - The breeding group to set
 * @param {boolean} allowOverride - If true, allows changing existing BreedingGroup (default: false)
 * @returns {Promise<{updated: boolean, reason: string}>} Result of the update operation
 */
export async function updateAnimalBreedingGroup(userID, animalMAC, breedingGroup, allowOverride = false) {
	try {
		// Get current animal data
		const animalDoc = await db
			.collection("Users")
			.doc(userID)
			.collection("Animals")
			.doc(animalMAC)
			.get();

		if (!animalDoc.exists) {
			throw new Error(`Animal with MAC ${animalMAC} not found`);
		}

		const animal = animalDoc.data();
		const currentBreedingGroup = animal.BreedingGroup;

		// Handle clearing BreedingGroup (null or empty string)
		if (breedingGroup === null || breedingGroup === "") {
			if (currentBreedingGroup && !allowOverride) {
				return {
					updated: false,
					reason: `Animal already has BreedingGroup "${currentBreedingGroup}". Use allowOverride=true to clear it.`
				};
			}
			
			// Delete the BreedingGroup field from Firestore
			await db
				.collection("Users")
				.doc(userID)
				.collection("Animals")
				.doc(animalMAC)
				.update({
					BreedingGroup: firebase.firestore.FieldValue.delete()
				});

			return {
				updated: true,
				reason: currentBreedingGroup ? `BreedingGroup cleared (was "${currentBreedingGroup}")` : "BreedingGroup cleared"
			};
		}

		// If animal already has a BreedingGroup and it's different, prevent change unless override is allowed
		if (currentBreedingGroup && currentBreedingGroup.trim() && currentBreedingGroup !== breedingGroup && !allowOverride) {
			return {
				updated: false,
				reason: `Animal already has BreedingGroup "${currentBreedingGroup}". Use allowOverride=true to change it.`
			};
		}

		// Update the BreedingGroup
		await updateAnimalFields(userID, animalMAC, { BreedingGroup: breedingGroup });

		return {
			updated: true,
			reason: currentBreedingGroup ? `BreedingGroup updated from "${currentBreedingGroup}" to "${breedingGroup}"` : `BreedingGroup set to "${breedingGroup}"`
		};
	} catch (error) {
		console.error("[animalService][updateAnimalBreedingGroup] - Error updating animal BreedingGroup:", error);
		handleFirestoreError(error);
		throw error;
	}
}

/**
 * Checks if a SelectionEarTag already exists in the system
 * @param {string} userID - The unique identifier for the user
 * @param {string} selectionEarTag - The selection ear tag to check
 * @returns {Promise<{exists: boolean, animalMAC?: string}>} Object indicating if tag exists and which animal has it
 */
export async function checkSelectionEarTagExists(userID, selectionEarTag) {
	try {
		if (!selectionEarTag || !selectionEarTag.trim()) {
			return { exists: false };
		}

		const normalizedTag = String(selectionEarTag).trim();

		// Check active Animals collection
		const activeSnapshot = await db
			.collection("Users")
			.doc(userID)
			.collection("Animals")
			.where("SelectionEarTag", "==", normalizedTag)
			.get();

		if (!activeSnapshot.empty) {
			const animalDoc = activeSnapshot.docs[0];
			return {
				exists: true,
				animalMAC: animalDoc.id
			};
		}

		// Check ArchivedAnimals collection
		const archivedSnapshot = await db
			.collection("Users")
			.doc(userID)
			.collection("ArchivedAnimals")
			.where("SelectionEarTag", "==", normalizedTag)
			.get();

		if (!archivedSnapshot.empty) {
			const animalDoc = archivedSnapshot.docs[0];
			return {
				exists: true,
				animalMAC: animalDoc.id,
				isArchived: true
			};
		}

		return { exists: false };
	} catch (error) {
		console.error("[animalService][checkSelectionEarTagExists] - Error checking selection ear tag:", error);
		// On error, return exists: false to allow creation (fail-safe)
		return { exists: false };
	}
}


/**
 * Marks an animal as healed by clearing the Diagnosed field and creating a recovery event
 * @param {string} userID - The unique identifier for the user
 * @param {string} animal - MAC address of the animal's device
 * @returns {Promise<void>}
 */
export async function markAnimalAsHealed(userID, animal) {
	try {
		await db
			.collection("Users")
			.doc(userID)
			.collection("Animals")
			.doc(animal)
			.update({
				Diagnosed: null,
			});

		// Create a recovery event in GeneralEvents collection
		const recoveryEvent = {
			ID: animal,
			Diagnose: "Recovered",
			Archived: false,
			Description: "Animal marked as recovered",
			MAC: animal,
			Uid: userID,
			t: new Date().getTime(),
		};

		await db
			.collection("Users")
			.doc(userID)
			.collection("GeneralEvents")
			.doc(recoveryEvent.t.toString())
			.set(recoveryEvent);
	} catch (error) {
		console.error("[animalService][markAnimalAsHealed] - Error marking animal as healed:", error);
		handleFirestoreError(error);
		throw error;
	}
}
